import axios from "axios";
import _ from "lodash";
import constants from "@/config/constants";

function initialState() {
    return {
        items: [],
        pagination: {
            page: 1,
            limit: 10,
            total: 0,
            pages: 0
        },
        loading: false,
        error: null,
        selectedItem: null
    }
}

const getters = {
    items: state => state.items,
    pagination: state => state.pagination,
    loading: state => state.loading,
    error: state => state.error,
    selectedItem: state => state.selectedItem,
    
    getItemById: (state) => (id) => {
        return state.items.find(item => item._id === id);
    },
    
    getItemsByModule: (state) => (moduleName) => {
        return state.items.filter(item => item.module_name === moduleName);
    }
}

const actions = {
    /**
     * Fetch all match making with pagination
     */
    async fetchAll({ commit, state }, { page = 1, limit = 10, filters = {} }) {
        commit('setLoading', true);
        commit('setError', null);
        
        const headers = {
            'Authorization': 'Bearer ' + localStorage.getItem('token')
        };
        
        const params = {
            page,
            limit,
            ...filters
        };
        
        try {
            const response = await axios.get(
                `${constants.API_URL}/api/matchmaking`,
                { headers, params }
            );
            
            const data = _.get(response, 'data.data', []);
            const pagination = _.get(response, 'data.pagination', {
                page: 1,
                limit: 10,
                total: 0,
                pages: 0
            });
            
            commit('setItems', data);
            commit('setPagination', pagination);
            
            return response;
        } catch (error) {
            commit('setError', error.response?.data?.message || 'Failed to fetch match making data');
            console.error('Error fetching match making:', error);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },
    
    /**
     * Fetch match making by event
     */
    async fetchByEvent({ commit }, { eventId, page = 1, limit = 10 }) {
        commit('setLoading', true);
        commit('setError', null);
        
        const headers = {
            'Authorization': 'Bearer ' + localStorage.getItem('token')
        };
        
        try {
            const response = await axios.get(
                `${constants.API_URL}/api/matchmaking/event/${eventId}?page=${page}&limit=${limit}`,
                { headers }
            );
            
            const data = _.get(response, 'data.data', {});
            commit('setSelectedItem', data);
            
            return response;
        } catch (error) {
            commit('setError', error.response?.data?.message || 'Failed to fetch match making by event');
            console.error('Error fetching match making by event:', error);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },
    
    /**
     * Fetch items by module
     */
    async fetchByModule({ commit }, { eventId, moduleName }) {
        commit('setLoading', true);
        commit('setError', null);
        
        const headers = {
            'Authorization': 'Bearer ' + localStorage.getItem('token')
        };
        
        try {
            const response = await axios.get(
                `${constants.API_URL}/api/matchmaking/${eventId}/${moduleName}`,
                { headers }
            );
            
            const data = _.get(response, 'data.data', {});
            commit('setSelectedItem', data);
            
            return response;
        } catch (error) {
            commit('setError', error.response?.data?.message || 'Failed to fetch match making by module');
            console.error('Error fetching match making by module:', error);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },
    
    /**
     * Bulk import match making data
     */
    async bulkImport({ commit }, { eventId, items }) {
        commit('setLoading', true);
        commit('setError', null);
        
        const headers = {
            'Authorization': 'Bearer ' + localStorage.getItem('token')
        };
        
        const params = {
            event_id: eventId,
            items: items
        };
        
        try {
            const response = await axios.post(
                `${constants.API_URL}/api/matchmaking/bulk`,
                params,
                { headers }
            );
            
            return response;
        } catch (error) {
            commit('setError', error.response?.data?.message || 'Failed to bulk import match making data');
            console.error('Error bulk importing match making:', error);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },
    
    /**
     * Search match making by keyword
     */
    async searchByKeyword({ commit }, { eventId, moduleName, keyword }) {
        commit('setLoading', true);
        commit('setError', null);
        
        const headers = {
            'Authorization': 'Bearer ' + localStorage.getItem('token')
        };
        
        try {
            const response = await axios.get(
                `${constants.API_URL}/api/matchmaking/search?event_id=${eventId}&module_name=${moduleName}&keyword=${keyword}`,
                { headers }
            );
            
            const data = _.get(response, 'data.data', []);
            commit('setItems', data);
            
            return response;
        } catch (error) {
            commit('setError', error.response?.data?.message || 'Failed to search match making');
            console.error('Error searching match making:', error);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },
    
    /**
     * Delete match making by event
     */
    async deleteByEvent({ commit }, eventId) {
        commit('setLoading', true);
        commit('setError', null);
        
        const headers = {
            'Authorization': 'Bearer ' + localStorage.getItem('token')
        };
        
        try {
            const response = await axios.delete(
                `${constants.API_URL}/api/matchmaking/event/${eventId}`,
                { headers }
            );
            
            commit('setSelectedItem', null);
            return response;
        } catch (error) {
            commit('setError', error.response?.data?.message || 'Failed to delete match making by event');
            console.error('Error deleting match making by event:', error);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },
    
    setSelectedItem({ commit }, item) {
        commit('setSelectedItem', item);
    },
    
    resetState({ commit }) {
        commit('resetState');
    }
}

const mutations = {
    setItems(state, items) {
        state.items = items;
    },
    
    setPagination(state, pagination) {
        state.pagination = { ...state.pagination, ...pagination };
    },
    
    setLoading(state, loading) {
        state.loading = loading;
    },
    
    setError(state, error) {
        state.error = error;
    },
    
    setSelectedItem(state, item) {
        state.selectedItem = item;
    },
    
    resetState(state) {
        const initial = initialState();
        state.items = initial.items;
        state.pagination = initial.pagination;
        state.loading = initial.loading;
        state.error = initial.error;
        state.selectedItem = initial.selectedItem;
    }
}

export default {
    namespaced: true,
    state: initialState,
    getters,
    actions,
    mutations
}